import type { FieldCondition, SchemaRule, StaticRule } from '@tasama/types';
import { isFieldStateRule } from './rule-guards';
import { compareOrdered, isFieldFilled } from './rule-evaluation.util';

function includesSubstring(actual: unknown, value: unknown): boolean {
  return String(actual ?? '')
    .toLowerCase()
    .includes(String(value ?? '').toLowerCase());
}

function conditionMatches(
  { field, op, value }: FieldCondition,
  values: Record<string, unknown>
): boolean {
  const actual = values[field];
  switch (op) {
    case 'eq':
      return actual === value;
    case 'neq':
      return actual !== value;
    case 'in':
      return Array.isArray(value) && value.includes(actual as string);
    case 'notIn':
      return Array.isArray(value) && !value.includes(actual as string);
    case 'gt':
    case 'lt':
    case 'gte':
    case 'lte':
      return compareOrdered(op, actual, value);
    case 'isEmpty':
      return !isFieldFilled(actual);
    case 'isNotEmpty':
      return isFieldFilled(actual);
    case 'like': // SNOW LIKE — case-insensitive substring
      return includesSubstring(actual, value);
    case 'notLike':
      return !includesSubstring(actual, value);
    default:
      return true;
  }
}

// Generalizes the reference demo's isFieldVisible (App.tsx:95-98) to an AND-list
// of field->field conditions per the R4 contract (§3.1).
export function conditionsMet(
  conditions: FieldCondition[] | undefined,
  values: Record<string, unknown>
): boolean {
  if (!conditions || conditions.length === 0) return true;
  return conditions.every((condition) => conditionMatches(condition, values));
}

// OR-of-AND — outer array is OR, inner array is AND (contract §1, "rare, 1 known case").
export function conditionGroupsMet(
  conditionGroups: FieldCondition[][] | undefined,
  values: Record<string, unknown>
): boolean {
  if (!conditionGroups || conditionGroups.length === 0) return true;
  return conditionGroups.some((group) =>
    group.every((condition) => conditionMatches(condition, values))
  );
}

// A rule with neither `conditions` nor `conditionGroups` is unconditional (permanent state).
// At most one of the two is expected to be present on a given rule.
function ruleConditionMet(
  rule: StaticRule,
  values: Record<string, unknown>
): boolean {
  if (rule.conditionGroups)
    return conditionGroupsMet(rule.conditionGroups, values);
  return conditionsMet(rule.conditions, values);
}

export interface FieldState {
  hidden: boolean;
  required: boolean;
  readOnly: boolean;
  defaultValue?: string | number | boolean;
  label?: string;
}

// Resolves one field's effective state by evaluating every top-level staticRules[] entry
// that targets it against the live form values — replaces the old per-field
// `conditions`/`effect` shape (contract v3) now that BE models rules as a separate
// top-level array (confirmed via the FE/BFF diff table).
export function resolveFieldState(
  fieldName: string,
  rules: SchemaRule[] | undefined,
  values: Record<string, unknown>
): FieldState {
  const state: FieldState = { hidden: false, required: false, readOnly: false };

  (rules ?? [])
    .filter(isFieldStateRule)
    .filter((rule) => rule.field === fieldName)
    .forEach((rule) => {
      if (!ruleConditionMet(rule, values)) return;
      switch (rule.effect) {
        case 'HIDE':
          state.hidden = true;
          break;
        case 'SHOW':
          state.hidden = false;
          break;
        case 'REQUIRE':
          state.required = true;
          break;
        case 'OPTIONAL':
          state.required = false;
          break;
        case 'READONLY':
          state.readOnly = true;
          break;
        case 'DEFAULT':
          if (rule.value !== undefined) state.defaultValue = rule.value;
          break;
        case 'LABEL':
          if (typeof rule.value === 'string') state.label = rule.value;
          break;
        default:
          break;
      }
    });

  return state;
}
